import { useKeycloak } from '@react-keycloak/web';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { UseMutateFunction, useQuery, useQueryClient } from '@tanstack/react-query';

import { LocationCommentsResponse } from '@/api/data-contracts';
import useCommentsService from '@/features/comments/api/comments.service';
import useCommentMutations from '@/features/comments/api/useCommentMutations';
import { useMediaPlansService } from '@/features/media-plans';
import { useOrdersService } from '@/features/orders';
import { useTasks } from '@/features/service-tasks';
import { queryKeys } from '@/react-query/constants';
import {
  getStoredChannelWatermark,
  getStoredWatermark,
  getWatermarkKey,
  setStoredChannelWatermark,
  setStoredWatermark,
} from '@/shared/lib/commentWatermarkUtils';
import { useSessionStableWatermarks } from '@/shared/lib/hooks/useSessionStableWatermarks';

import { resolveCommentSaveAction } from './commentSaveAction';
import { buildCommentTabs, buildCommentThreads } from './commentThreads';
import { CommentWithUnread, injectIsUnread } from './commentUtils';

export interface UseCommentsProps {
  orderId?: string | undefined;
  locationId?: string | undefined;
  mediaPlanId?: string | undefined;
  taskId?: string | undefined;
  bookingId?: string | undefined;
  attachmentId?: string | undefined;
  channel?: string | undefined;
  useServicesEndpoints?: boolean;
  deferOrderReadMark?: boolean;
  deferMediaPlanReadMark?: boolean;
  userType?: 'location' | 'advertiser' | 'service' | 'backoffice' | undefined;
  isInternalOrder?: boolean;
}

export interface UseCommentsReturn {
  saveComment: (args: {
    message: string;
    files?: File[];
    commentId?: string;
    parentId?: string;
    edit?: boolean;
    coordinates?: number[];
  }) => void;
  deleteComment: UseMutateFunction<void, unknown, string, unknown>;
  commentThreads: Record<string, CommentWithUnread[]> | undefined;
  isLoading: boolean;
  hasUnread: boolean;
  tabs?:
    | {
        locationId: string;
        commercialName: string;
        hasUnread: boolean;
      }[]
    | undefined;
  currentUserId: string | undefined;
  markOrderAsRead: () => void;
  markChannelRead: () => void;
  downloadCommentFile: (resourceId: string, documentId: string) => Promise<Blob>;
}

type LocationCommentsWithUnread = Omit<LocationCommentsResponse, 'comments'> & {
  comments: Record<string, CommentWithUnread[]>;
};

const useComments = ({
  orderId,
  locationId,
  mediaPlanId,
  taskId,
  bookingId,
  attachmentId,
  channel,
  useServicesEndpoints,
  deferOrderReadMark,
  deferMediaPlanReadMark,
  userType,
  isInternalOrder: _isInternalOrder,
}: UseCommentsProps): UseCommentsReturn => {
  const queryClient = useQueryClient();
  const { getComments: getMediaPlanComments, downloadMediaPlanCommentDocument } =
    useMediaPlansService();
  const { getCommentWatermark } = useCommentsService();
  const {
    getServiceTaskComments: getTaskComments,
    getServiceTaskAttachmentComments: getAttachmentComments,
    downloadTaskCommentDocument,
    downloadServicesTaskCommentDocument,
  } = useTasks();
  const {
    getOrderComments,
    getServicesOrderComments,
    downloadOrderCommentDocument,
    downloadServicesOrderCommentDocument,
  } = useOrdersService();
  const { keycloak } = useKeycloak();
  const currentUserId = keycloak.tokenParsed?.sub as string | undefined;

  const { getStableWatermark, clearSnapshot } = useSessionStableWatermarks();
  const markedAsRead = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (mediaPlanId) {
      clearSnapshot('MEDIA_PLAN', mediaPlanId);
      markedAsRead.current.delete(`mediaPlan-${mediaPlanId}`);
    }
  }, [mediaPlanId, clearSnapshot]);

  useEffect(() => {
    if (orderId && locationId) {
      clearSnapshot('ORDER', orderId, locationId);
      markedAsRead.current.delete(`order-${orderId}-${locationId}`);
    }
  }, [orderId, locationId, clearSnapshot]);

  useEffect(() => {
    if (taskId && attachmentId) {
      clearSnapshot('ATTACHMENT', taskId, attachmentId);
      markedAsRead.current.delete(`attachment-${taskId}-${attachmentId}`);
    } else if (taskId) {
      clearSnapshot('TASK', bookingId ?? taskId);
      markedAsRead.current.delete(`task-${bookingId ?? taskId}`);
    }
  }, [taskId, attachmentId, bookingId, clearSnapshot]);

  const { data: mediaPlanComments, isLoading: isLoadingMediaPlan } = useQuery<
    Record<string, CommentWithUnread[]>
  >(
    [queryKeys.comments, 'mediaPlan', mediaPlanId],
    async ({ signal }) => {
      let comments = {};
      let watermark: string | null = null;
      try {
        [comments, watermark] = await Promise.all([
          getMediaPlanComments(mediaPlanId!, signal),
          getCommentWatermark('MEDIA_PLAN', mediaPlanId!, undefined, signal).then(
            (r) => r.lastReadAt,
          ),
        ]);
      } catch {
        // Fallback to local storage if backend fails
      }
      const stored = getStoredWatermark(getWatermarkKey('MEDIA_PLAN', mediaPlanId!));
      const stableWatermark = getStableWatermark(
        'MEDIA_PLAN',
        mediaPlanId!,
        null,
        watermark,
        stored,
      );
      return injectIsUnread(comments, stableWatermark, currentUserId ?? '');
    },
    { enabled: !!mediaPlanId, refetchInterval: 30000, refetchIntervalInBackground: false },
  );

  const { data: taskComments, isLoading: isLoadingTask } = useQuery<
    Record<string, CommentWithUnread[]>
  >(
    [queryKeys.comments, 'task', taskId],
    async ({ signal }) => {
      let comments = {};
      let watermark: string | null = null;
      try {
        [comments, watermark] = await Promise.all([
          getTaskComments(taskId!, signal),
          getCommentWatermark('TASK', taskId!, undefined, signal).then((r) => r.lastReadAt),
        ]);
      } catch {
        // ignore
      }
      const stored = getStoredWatermark(getWatermarkKey('TASK', bookingId ?? taskId!));
      const stableWatermark = getStableWatermark(
        'TASK',
        bookingId ?? taskId!,
        null,
        watermark,
        stored,
      );
      return injectIsUnread(comments, stableWatermark, currentUserId ?? '');
    },
    {
      enabled: !!taskId && !attachmentId,
      refetchInterval: 30000,
      refetchIntervalInBackground: false,
    },
  );

  const { data: attachmentComments, isLoading: isLoadingAttachment } = useQuery<
    Record<string, CommentWithUnread[]>
  >(
    [queryKeys.comments, 'attachments', taskId, attachmentId],
    async ({ signal }) => {
      let comments = {};
      let watermark: string | null = null;
      try {
        [comments, watermark] = await Promise.all([
          getAttachmentComments(taskId!, attachmentId!, signal),
          getCommentWatermark('ATTACHMENT', taskId!, attachmentId!, signal).then(
            (r) => r.lastReadAt,
          ),
        ]);
      } catch {
        // ignore
      }
      const stored = getStoredWatermark(getWatermarkKey('ATTACHMENT', taskId!, attachmentId!));
      const stableWatermark = getStableWatermark(
        'ATTACHMENT',
        taskId!,
        attachmentId!,
        watermark,
        stored,
      );
      return injectIsUnread(comments, stableWatermark, currentUserId ?? '');
    },
    {
      enabled: !!taskId && !!attachmentId,
      refetchInterval: 30000,
      refetchIntervalInBackground: false,
    },
  );

  const { data: orderComments, isLoading: isLoadingOrder } = useQuery<LocationCommentsWithUnread[]>(
    [queryKeys.comments, 'order', orderId, locationId, channel],
    async ({ signal }) => {
      let commentsResponse: LocationCommentsResponse[] = [];
      try {
        commentsResponse = await getOrderComments(orderId!, channel, locationId, signal);
      } catch {
        return [];
      }
      const watermarks = await Promise.all(
        commentsResponse.map(async (loc) => {
          let w: string | null = null;
          try {
            w = (await getCommentWatermark('ORDER', orderId!, loc.locationId, signal)).lastReadAt;
          } catch {
            // ignore
          }
          return {
            locationId: loc.locationId,
            watermark: w,
          };
        }),
      );
      return commentsResponse.map((loc) => {
        const watermarkInfo = watermarks.find((w) => w.locationId === loc.locationId);
        const stored = channel
          ? getStoredChannelWatermark('ORDER', orderId!, loc.locationId, channel)
          : getStoredWatermark(getWatermarkKey('ORDER', orderId!, loc.locationId));
        const stableWatermark = getStableWatermark(
          'ORDER',
          orderId!,
          loc.locationId,
          watermarkInfo?.watermark,
          stored,
        );
        return {
          ...loc,
          comments: injectIsUnread(loc.comments ?? {}, stableWatermark, currentUserId ?? ''),
        };
      });
    },
    {
      enabled: !!orderId && !useServicesEndpoints,
      cacheTime: 0,
      refetchInterval: 30000,
      refetchIntervalInBackground: false,
    },
  );

  const { data: servicesOrderComments, isLoading: isLoadingServicesOrder } = useQuery<
    LocationCommentsWithUnread[]
  >(
    [queryKeys.comments, 'services-order', orderId, channel],
    async ({ signal }) => {
      let commentsResponse: LocationCommentsResponse[] = [];
      try {
        commentsResponse = await getServicesOrderComments(orderId!, channel, signal);
      } catch {
        return [];
      }
      const stored = getStoredWatermark(getWatermarkKey('SERVICES_ORDER', orderId!));
      return commentsResponse.map((loc) => ({
        ...loc,
        comments: injectIsUnread(loc.comments ?? {}, stored, currentUserId ?? ''),
      }));
    },
    {
      enabled: !!orderId && !!useServicesEndpoints,
      cacheTime: 0,
      refetchInterval: 30000,
      refetchIntervalInBackground: false,
    },
  );

  useEffect(() => {
    if (mediaPlanId && mediaPlanComments && !deferMediaPlanReadMark) {
      const key = `mediaPlan-${mediaPlanId}`;
      if (!markedAsRead.current.has(key)) {
        markedAsRead.current.add(key);
        setStoredWatermark(getWatermarkKey('MEDIA_PLAN', mediaPlanId), new Date().toISOString());
      }
    }
  }, [mediaPlanId, mediaPlanComments, deferMediaPlanReadMark]);

  useEffect(() => {
    if (orderId && locationId && orderComments && !deferOrderReadMark) {
      const key = `order-${orderId}-${locationId}`;
      if (!markedAsRead.current.has(key)) {
        markedAsRead.current.add(key);
        setStoredWatermark(getWatermarkKey('ORDER', orderId, locationId), new Date().toISOString());
      }
    }
  }, [orderId, locationId, orderComments, deferOrderReadMark]);

  // Locations to stamp for the active channel: the explicit location when scoped to one,
  // otherwise every location group in the current channel's response.
  const activeChannelLocationIds = useCallback(
    (): (string | undefined)[] =>
      locationId ? [locationId] : (orderComments ?? []).map((loc) => loc.locationId),
    [locationId, orderComments],
  );

  const markOrderAsRead = useCallback(() => {
    const now = new Date().toISOString();

    if (mediaPlanId) {
      clearSnapshot('MEDIA_PLAN', mediaPlanId);
      setStoredWatermark(getWatermarkKey('MEDIA_PLAN', mediaPlanId), now);
      queryClient.invalidateQueries({
        queryKey: [queryKeys.comments, 'mediaPlan', mediaPlanId, 'unreadCount'],
      });
      return;
    }

    if (!orderId) return;

    if (useServicesEndpoints) {
      setStoredWatermark(getWatermarkKey('SERVICES_ORDER', orderId), now);
      queryClient.setQueriesData<number | undefined>(
        { queryKey: [queryKeys.comments, 'services-order', orderId] },
        (data) => (typeof data === 'number' ? 0 : data),
      );
      queryClient.invalidateQueries({ queryKey: [queryKeys.comments, 'services-order', orderId] });
      return;
    }

    if (channel) {
      // Channel-scoped keys make it safe to mark any channel — including locationless ones
      // like team — without masking unread state in the sibling channels of a location.
      activeChannelLocationIds().forEach((locId) => {
        clearSnapshot('ORDER', orderId, locId);
        setStoredChannelWatermark('ORDER', orderId, locId, channel, now);
      });
      queryClient.invalidateQueries({ queryKey: [queryKeys.comments, 'order', orderId] });
      return;
    }

    // No channel context (legacy consumers): keep the old shared-location-key behavior.
    if (locationId) {
      clearSnapshot('ORDER', orderId, locationId);
      setStoredWatermark(getWatermarkKey('ORDER', orderId, locationId), now);
      queryClient.invalidateQueries({
        queryKey: [queryKeys.comments, 'order', orderId, locationId, channel],
      });
    }
  }, [
    orderId,
    locationId,
    mediaPlanId,
    channel,
    activeChannelLocationIds,
    useServicesEndpoints,
    clearSnapshot,
    queryClient,
  ]);

  // Like markOrderAsRead, but for while the panel is open on a channel: advances the
  // channel's watermark and refreshes only the unread-count queries, leaving the
  // session-stable snapshots alone so the open panel keeps its "new" highlights.
  const markChannelRead = useCallback(() => {
    const now = new Date().toISOString();

    if (mediaPlanId) {
      setStoredWatermark(getWatermarkKey('MEDIA_PLAN', mediaPlanId), now);
      queryClient.invalidateQueries({
        queryKey: [queryKeys.comments, 'mediaPlan', mediaPlanId, 'unreadCount'],
      });
      return;
    }

    if (!orderId || useServicesEndpoints || !channel) return;

    activeChannelLocationIds().forEach((locId) => {
      setStoredChannelWatermark('ORDER', orderId, locId, channel, now);
    });
    queryClient.invalidateQueries({
      predicate: (query) =>
        Array.isArray(query.queryKey) &&
        query.queryKey[0] === queryKeys.comments &&
        query.queryKey.includes(orderId) &&
        query.queryKey.includes('unreadCount'),
    });
  }, [orderId, mediaPlanId, channel, activeChannelLocationIds, useServicesEndpoints, queryClient]);

  useEffect(() => {
    if (taskId && attachmentId && attachmentComments) {
      const key = `attachment-${taskId}-${attachmentId}`;
      if (!markedAsRead.current.has(key)) {
        markedAsRead.current.add(key);
        setStoredWatermark(
          getWatermarkKey('ATTACHMENT', taskId, attachmentId),
          new Date().toISOString(),
        );
      }
    } else if (taskId && !attachmentId && taskComments) {
      const key = `task-${bookingId ?? taskId}`;
      if (!markedAsRead.current.has(key)) {
        markedAsRead.current.add(key);
        setStoredWatermark(getWatermarkKey('TASK', bookingId ?? taskId), new Date().toISOString());
        if (bookingId) {
          queryClient.invalidateQueries({
            queryKey: [queryKeys.comments, 'task-unread', bookingId],
          });
        }
      }
    }
  }, [taskId, attachmentId, bookingId, taskComments, attachmentComments, queryClient]);

  const isLoading =
    (!!mediaPlanId && isLoadingMediaPlan) ||
    (!!taskId && !attachmentId && isLoadingTask) ||
    (!!taskId && !!attachmentId && isLoadingAttachment) ||
    (!!orderId && !useServicesEndpoints && isLoadingOrder) ||
    (!!orderId && !!useServicesEndpoints && isLoadingServicesOrder);

  const tabs = useMemo(() => buildCommentTabs(orderComments), [orderComments]);

  const commentThreads = useMemo(
    () =>
      buildCommentThreads(
        {
          taskComments,
          mediaPlanComments,
          attachmentComments,
          orderComments,
          servicesOrderComments,
        },
        { taskId, attachmentId, mediaPlanId, orderId, locationId, useServicesEndpoints },
      ),
    [
      attachmentComments,
      attachmentId,
      locationId,
      mediaPlanComments,
      mediaPlanId,
      orderComments,
      orderId,
      servicesOrderComments,
      taskComments,
      taskId,
      useServicesEndpoints,
    ],
  );

  const {
    saveMediaPlanComment,
    saveOrderComment,
    saveServicesOrderComment,
    saveTaskComment,
    saveAttachmentComment,
    editMediaPlanCommentMutation,
    editOrderComment,
    editTaskCommentMutation,
    deleteComment,
  } = useCommentMutations({
    mediaPlanId,
    orderId,
    locationId,
    taskId,
    bookingId,
    attachmentId,
    channel,
    userType,
    useServicesEndpoints,
  });

  const saveComment = useCallback(
    ({
      message,
      files,
      commentId,
      parentId,
      edit,
      coordinates,
    }: {
      message: string;
      files?: File[];
      commentId?: string;
      parentId?: string;
      edit?: boolean;
      coordinates?: number[];
    }) => {
      const action = resolveCommentSaveAction(
        { mediaPlanId, orderId, locationId, taskId, attachmentId, channel, useServicesEndpoints },
        { message, files, commentId, parentId, edit, coordinates },
      );
      if (!action) return;

      switch (action.type) {
        case 'editMediaPlan':
          editMediaPlanCommentMutation(action.payload);
          break;
        case 'editOrder':
          editOrderComment(action.payload);
          break;
        case 'editTask':
          editTaskCommentMutation(action.payload);
          break;
        case 'saveMediaPlan':
          saveMediaPlanComment(action.payload);
          break;
        case 'saveAttachment':
          saveAttachmentComment(action.payload);
          break;
        case 'saveTask':
          saveTaskComment(action.payload);
          break;
        case 'saveServicesOrder':
          saveServicesOrderComment(action.payload);
          break;
        case 'saveOrder':
          saveOrderComment(action.payload);
          break;
      }
    },
    [
      attachmentId,
      channel,
      editMediaPlanCommentMutation,
      editOrderComment,
      editTaskCommentMutation,
      locationId,
      mediaPlanId,
      orderId,
      saveAttachmentComment,
      saveMediaPlanComment,
      saveOrderComment,
      saveServicesOrderComment,
      saveTaskComment,
      taskId,
      useServicesEndpoints,
    ],
  );

  const hasUnread = useMemo(() => {
    if (tabs.some((t) => t.hasUnread)) return true;
    const flatComments = (data: Record<string, CommentWithUnread[]> | undefined) =>
      Object.values(data ?? {})
        .flat()
        .some((c) => c.isUnread);
    return (
      flatComments(mediaPlanComments) ||
      flatComments(taskComments) ||
      flatComments(attachmentComments)
    );
  }, [tabs, mediaPlanComments, taskComments, attachmentComments]);

  const downloadCommentFile = useCallback(
    (resourceId: string, documentId: string): Promise<Blob> => {
      if (mediaPlanId) {
        return downloadMediaPlanCommentDocument(resourceId, documentId);
      } else if (useServicesEndpoints && orderId) {
        return downloadServicesOrderCommentDocument(resourceId, documentId);
      } else if (taskId && useServicesEndpoints) {
        return downloadServicesTaskCommentDocument(resourceId, documentId);
      } else if (taskId) {
        return downloadTaskCommentDocument(resourceId, documentId);
      } else {
        return downloadOrderCommentDocument(resourceId, documentId);
      }
    },
    [
      mediaPlanId,
      orderId,
      taskId,
      useServicesEndpoints,
      downloadMediaPlanCommentDocument,
      downloadOrderCommentDocument,
      downloadServicesOrderCommentDocument,
      downloadTaskCommentDocument,
      downloadServicesTaskCommentDocument,
    ],
  );

  return {
    commentThreads,
    saveComment,
    deleteComment,
    tabs,
    isLoading,
    hasUnread,
    markOrderAsRead,
    markChannelRead,
    currentUserId,
    downloadCommentFile,
  };
};

export default useComments;
